import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, assetUrl, errText } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { BRAND_PALETTE, contrastRatio } from "../lib/colors";
import { MessageSquarePlus, Clock, ShieldCheck, UserPlus, Trash2, Crown, Layers, Plus, Check, ImagePlus, Loader2, Pipette, AlertTriangle, RotateCcw } from "lucide-react";

const WM_POS = [
  { v: "top-left", l: "TL" }, { v: "top-center", l: "TC" }, { v: "top-right", l: "TR" },
  { v: "bottom-left", l: "BL" }, { v: "bottom-center", l: "BC" }, { v: "bottom-right", l: "BR" },
];

function wmBoxStyle(pos, scale, op) {
  const s = { position: "absolute", width: `${(Math.max(120, Math.min(scale, 560)) / 1080) * 100}%`, opacity: op };
  const side = pos.startsWith("top") ? "5.5%" : "11%";
  if (pos.startsWith("bottom")) s.bottom = pos === "bottom-center" ? "12.5%" : "13.5%"; else s.top = "6.25%";
  if (pos.endsWith("right")) s.right = side; else if (pos.endsWith("left")) s.left = side; else { s.left = "50%"; s.transform = "translateX(-50%)"; }
  return s;
}

function WatermarkControls({ b, patchBrand, applyWmToAll, otherBrandCount }) {
  const pos = b.wm_position || "bottom-right";
  const [scale, setScale] = useState(b.wm_scale || 300);
  const [op, setOp] = useState(b.wm_opacity ?? 0.6);
  useEffect(() => { setScale(b.wm_scale || 300); }, [b.wm_scale]);
  useEffect(() => { setOp(b.wm_opacity ?? 0.6); }, [b.wm_opacity]);
  const isDefault = !b.wm_position && !b.wm_scale && b.wm_opacity == null;
  const wmUrl = `${process.env.REACT_APP_BACKEND_URL}/api/brands/${b.id}/watermark.png?t=${b.logo_url || ""}${b.wordmark_url || ""}`;
  const saveWm = async (patch, msg) => {
    await patchBrand(b, patch);
    toast.success(msg || "Watermark saved");
  };
  return (
    <div className="space-y-2 pt-2 border-t border-border/50" data-testid={`wm-controls-${b.id}`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">Watermark placement</p>
        <button type="button" data-testid={`wm-reset-${b.id}`} disabled={isDefault}
          onClick={() => saveWm({ wm_position: null, wm_scale: null, wm_opacity: null }, "Watermark reset to default")}
          className="text-[10px] flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-40">
          <RotateCcw size={10} /> Reset
        </button>
      </div>
      <div className="flex gap-3">
        {/* Live 9:16 preview with the real watermark image */}
        <div className="relative w-[76px] shrink-0 aspect-[9/16] rounded-md overflow-hidden bg-neutral-800 border border-border" data-testid={`wm-preview-${b.id}`}>
          <div className="absolute top-0 bottom-0 right-0 bg-red-500/15" style={{ width: "12%" }} />
          <div className="absolute left-0 right-0 bottom-0 bg-amber-400/15" style={{ height: "16%" }} />
          <img src={wmUrl} alt="" style={wmBoxStyle(pos, scale, op)}
            onError={(e) => { e.target.style.display = "none"; }} />
        </div>
        <div className="flex-1 space-y-2">
          <div className="grid grid-cols-3 gap-1">
            {WM_POS.map(({ v, l }) => (
              <button key={v} data-testid={`wm-pos-${b.id}-${v}`} onClick={() => saveWm({ wm_position: v })}
                className={`text-[10px] py-1 rounded border transition-colors ${pos === v ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground w-[78px] shrink-0">Size {scale}px</span>
            <input type="range" min="160" max="500" step="10" value={scale} data-testid={`wm-scale-${b.id}`}
              onChange={(e) => setScale(+e.target.value)}
              onMouseUp={() => saveWm({ wm_scale: scale })} onTouchEnd={() => saveWm({ wm_scale: scale })}
              className="flex-1 accent-accent h-1" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground w-[78px] shrink-0">Opacity {Math.round(op * 100)}%</span>
            <input type="range" min="0.2" max="1" step="0.05" value={op} data-testid={`wm-opacity-${b.id}`}
              onChange={(e) => setOp(+e.target.value)}
              onMouseUp={() => saveWm({ wm_opacity: op })} onTouchEnd={() => saveWm({ wm_opacity: op })}
              className="flex-1 accent-accent h-1" />
          </div>
        </div>
      </div>
      {otherBrandCount > 0 && (
        <button type="button" data-testid={`wm-apply-all-${b.id}`}
          onClick={() => applyWmToAll(b)}
          className="w-full text-[10.5px] flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-accent/60 transition-colors">
          <Layers size={11} /> Apply this placement to all {otherBrandCount + 1} brands
        </button>
      )}
    </div>
  );
}

const LOW_CONTRAST = 1.4;
function ContrastWarning({ primary, secondary, testid }) {
  const r = contrastRatio(primary, secondary);
  if (r === null || r >= LOW_CONTRAST) return null;
  return (
    <p className="text-[11px] text-amber-600 flex items-start gap-1" data-testid={testid}>
      <AlertTriangle size={12} className="shrink-0 mt-0.5" />
      Primary &amp; secondary look very similar — they may be hard to tell apart on charts and chips.
    </p>
  );
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function ColorField({ label, value, onCommit, testid }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const safe = HEX_RE.test(v) ? v : "#cccccc";
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-muted-foreground w-[70px] shrink-0">{label}</span>
      <input type="color" value={safe} data-testid={`${testid}-picker`}
        onChange={(e) => { setV(e.target.value); onCommit(e.target.value); }}
        className="w-7 h-7 rounded cursor-pointer border border-border bg-transparent p-0 shrink-0" />
      <input type="text" value={v} placeholder="#rrggbb" data-testid={`${testid}-hex`}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => HEX_RE.test(v) && v !== value && onCommit(v)}
        onKeyDown={(e) => { if (e.key === "Enter" && HEX_RE.test(v)) onCommit(v); }}
        className="w-24 h-7 text-xs font-mono rounded border border-border bg-background px-2" />
    </div>
  );
}

const TOGGLES = [
  { key: "first_comment_enabled", label: "First-comment automation", desc: "Auto-publish CTA links, extra hashtags & promo codes as the first comment on every published post to protect organic reach.", icon: MessageSquarePlus },
  { key: "best_time_enabled", label: "Dynamic best time to post", desc: "Automatically shift scheduled slots toward your highest-engagement hours from the analytics heatmap.", icon: Clock },
  { key: "spam_filter_enabled", label: "Moderation & spam filters", desc: "Auto-flag toxic comments, spam links and bot inquiries in the unified inbox.", icon: ShieldCheck },
];

export default function Settings() {
  const { user } = useAuth();
  const { refreshBrands } = useBrand();
  const [ws, setWs] = useState(null);
  const [team, setTeam] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState({ name: "", description: "", color: BRAND_PALETTE[0], secondary_color: "" });
  const logoRef = useRef();
  const [logoTarget, setLogoTarget] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setWs(r.data));
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
  }, []);

  const addBrand = async () => {
    if (!newBrand.name.trim()) return toast.error("Enter a brand name");
    try {
      const { data } = await api.post("/brands", newBrand);
      setBrands([...brands, data]);
      setNewBrand({ name: "", description: "", color: BRAND_PALETTE[(brands.length + 1) % BRAND_PALETTE.length], secondary_color: "" });
      refreshBrands();
      toast.success(`${data.name} added — it now has its own brand workspace in the sidebar`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const removeBrand = async (b) => {
    await api.delete(`/brands/${b.id}`);
    setBrands(brands.filter((x) => x.id !== b.id));
    refreshBrands();
    toast.success(`${b.name} removed`);
  };

  const applyWmToAll = async (source) => {
    const patch = {
      wm_position: source.wm_position || null,
      wm_scale: source.wm_scale || null,
      wm_opacity: source.wm_opacity ?? null,
    };
    const others = brands.filter((x) => x.id !== source.id);
    if (!others.length) return toast.info("This is your only brand — nothing to copy to");
    try {
      const results = await Promise.all(others.map((x) => api.patch(`/brands/${x.id}`, patch).then((r) => r.data)));
      setBrands((prev) => prev.map((x) => results.find((r) => r.id === x.id) || x));
      refreshBrands();
      toast.success(`Watermark placement applied to ${others.length} other brand${others.length === 1 ? "" : "s"}`);
    } catch (e) {
      refreshBrands();
      toast.error(errText(e));
    }
  };

  const patchBrand = async (b, patch) => {
    const { data } = await api.patch(`/brands/${b.id}`, patch);
    setBrands((prev) => prev.map((x) => (x.id === b.id ? data : x)));
    refreshBrands();
    return data;
  };

  const [extracting, setExtracting] = useState(null);
  const [extracted, setExtracted] = useState({});
  const extractColors = async (b) => {
    setExtracting(b.id);
    try {
      const { data } = await api.post(`/brands/${b.id}/extract-colors`);
      setExtracted((prev) => ({ ...prev, [b.id]: data.palette }));
      await patchBrand(b, { color: data.primary, ...(data.secondary ? { secondary_color: data.secondary } : {}) });
      toast.success(`Pulled ${data.palette.length} colour${data.palette.length === 1 ? "" : "s"} from ${b.name}'s logo & wordmark`);
    } catch (e) {
      toast.error(errText(e), { duration: Infinity, closeButton: true });
    } finally {
      setExtracting(null);
    }
  };

  const pickLogo = (b) => {
    setLogoTarget(b.id);
    setTimeout(() => logoRef.current?.click(), 0);
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !logoTarget) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/brands/${logoTarget}/logo`, fd);
      setBrands(brands.map((x) => (x.id === data.id ? data : x)));
      refreshBrands();
      toast.success(`${data.name} logo updated`);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const addEmail = async () => {
    if (!newEmail.trim()) return;
    try {
      const { data } = await api.post("/team", { email: newEmail });
      setTeam({ ...team, members: [...team.members, data] });
      setNewEmail("");
      toast.success(`${data.email} can now sign in with Google`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const removeEmail = async (email) => {
    try {
      await api.delete(`/team/${encodeURIComponent(email)}`);
      setTeam({ ...team, members: team.members.filter((m) => m.email !== email) });
      toast.success(`${email} removed — their sessions are revoked`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const toggle = async (key, val) => {
    const { data } = await api.patch("/settings", { [key]: val });
    setWs(data);
    toast.success("Setting saved");
  };

  if (!ws) return <p className="text-muted-foreground">Loading...</p>;

  return (
    <div className="space-y-8 fade-up max-w-3xl" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">Settings</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Automation & workspace.</h1>
      </div>

      <Card data-testid="brands-settings-card">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Layers size={18} /> Brands</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">The brands under Harmony Wellness Group. Each brand gets its own workspace (switch in the sidebar) with its own social accounts, accent colour and logo.</p>
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="brand-logo-input" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {brands.map((b) => (
              <div key={b.id} className="p-4 rounded-xl border border-border bg-muted/50 space-y-3" style={{ borderLeft: `4px solid ${b.color || "transparent"}` }} data-testid={`sub-brand-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    {b.logo_url ? (
                      <img src={assetUrl(b.logo_url)} alt={`${b.name} logo`} className="w-10 h-10 rounded-full object-cover border border-border shrink-0" />
                    ) : (
                      <span className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-white font-semibold" style={{ background: b.color }}>{b.name.charAt(0)}</span>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{b.name}</p>
                      {b.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{b.description}</p>}
                    </div>
                  </div>
                  <div className="flex shrink-0">
                    <Button variant="ghost" size="icon" data-testid={`upload-logo-${b.id}`} onClick={() => pickLogo(b)} aria-label={`Upload ${b.name} logo`}><ImagePlus size={14} /></Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-brand-${b.id}`} onClick={() => removeBrand(b)} aria-label={`Remove ${b.name}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <ColorField label="Primary" value={b.color} testid={`brand-primary-${b.id}`}
                    onCommit={(hex) => patchBrand(b, { color: hex })} />
                  <ColorField label="Secondary" value={b.secondary_color} testid={`brand-secondary-${b.id}`}
                    onCommit={(hex) => patchBrand(b, { secondary_color: hex })} />
                  <ContrastWarning primary={b.color} secondary={b.secondary_color} testid={`contrast-warn-${b.id}`} />
                  <WatermarkControls b={b} patchBrand={patchBrand} applyWmToAll={applyWmToAll} otherBrandCount={brands.length - 1} />
                  <div className="flex gap-1.5 flex-wrap items-center">
                    <span className="text-[11px] text-muted-foreground w-[70px] shrink-0">Presets</span>
                    {BRAND_PALETTE.map((c) => (
                      <button key={c} data-testid={`brand-color-${b.id}-${c.slice(1)}`} onClick={() => patchBrand(b, { color: c })} aria-label={`Set colour ${c}`}
                        className="w-5 h-5 rounded-full border border-black/10 flex items-center justify-center transition-transform hover:scale-110" style={{ background: c }}>
                        {b.color === c && <Check size={11} className="text-white drop-shadow" />}
                      </button>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" className="text-[11px] h-7" data-testid={`extract-colors-${b.id}`}
                    disabled={extracting === b.id} onClick={() => extractColors(b)}>
                    {extracting === b.id
                      ? <><Loader2 size={11} className="mr-1 animate-spin" /> Extracting…</>
                      : <><Pipette size={11} className="mr-1" /> Auto from logo &amp; wordmark</>}
                  </Button>
                  {extracted[b.id]?.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap items-center">
                      <span className="text-[11px] text-muted-foreground w-[70px] shrink-0">Extracted</span>
                      {extracted[b.id].map((c) => (
                        <button key={c} data-testid={`extracted-color-${b.id}-${c.slice(1)}`} title={`${c} — click to set primary`}
                          onClick={() => patchBrand(b, { color: c })}
                          className="w-5 h-5 rounded-full border border-black/10 hover:scale-110 transition-transform" style={{ background: c }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {brands.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No brands yet — add your first below.</p>}
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <Input data-testid="new-brand-name-input" className="max-w-[220px]" placeholder="Brand name" value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} />
              <Input data-testid="new-brand-desc-input" className="flex-1 min-w-[220px]" placeholder="Short description (optional)" value={newBrand.description} onChange={(e) => setNewBrand({ ...newBrand, description: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addBrand()} />
              <Button data-testid="add-brand-btn" onClick={addBrand}><Plus size={14} className="mr-2" /> Add brand</Button>
            </div>
            <div className="flex flex-col gap-2">
              <ColorField label="Primary" value={newBrand.color} testid="new-brand-primary"
                onCommit={(hex) => setNewBrand((p) => ({ ...p, color: hex }))} />
              <ColorField label="Secondary" value={newBrand.secondary_color} testid="new-brand-secondary"
                onCommit={(hex) => setNewBrand((p) => ({ ...p, secondary_color: hex }))} />
              <ContrastWarning primary={newBrand.color} secondary={newBrand.secondary_color} testid="new-brand-contrast-warn" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-muted-foreground w-[70px] shrink-0">Presets</span>
                {BRAND_PALETTE.map((c) => (
                  <button key={c} data-testid={`new-brand-color-${c.slice(1)}`} onClick={() => setNewBrand({ ...newBrand, color: c })} aria-label={`Choose ${c}`}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${newBrand.color === c ? "border-foreground scale-110" : "border-transparent"}`} style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-xl">Automation</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {TOGGLES.map((t) => (
            <div key={t.key} className="flex items-start justify-between gap-6">
              <div className="flex gap-3">
                <div className="p-2 rounded-lg bg-secondary text-secondary-foreground h-fit"><t.icon size={16} /></div>
                <div>
                  <Label className="font-semibold">{t.label}</Label>
                  <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
                </div>
              </div>
              <Switch data-testid={`toggle-${t.key}`} checked={ws.settings?.[t.key] ?? true} onCheckedChange={(v) => toggle(t.key, v)} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card data-testid="team-access-card">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><UserPlus size={18} /> Team access</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">Only Google accounts listed here can sign in to this workspace.</p>
          <div className="flex gap-3 flex-wrap">
            <Input data-testid="team-email-input" className="max-w-sm" type="email" placeholder="teammate@gmail.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEmail()} />
            <Button data-testid="add-team-email-btn" onClick={addEmail}><UserPlus size={14} className="mr-2" /> Authorise</Button>
          </div>
          <div className="space-y-2">
            {team?.owners.map((email) => (
              <div key={email} className="flex items-center justify-between p-3 rounded-lg bg-muted" data-testid={`team-owner-${email}`}>
                <span className="text-sm font-medium flex items-center gap-2"><Crown size={14} className="text-accent" /> {email}</span>
                <Badge variant="secondary">Owner</Badge>
              </div>
            ))}
            {team?.members.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg bg-muted" data-testid={`team-member-${m.email}`}>
                <span className="text-sm font-medium">{m.email}</span>
                <Button variant="ghost" size="icon" data-testid={`remove-team-${m.email}`} onClick={() => removeEmail(m.email)} aria-label={`Remove ${m.email}`}><Trash2 size={14} /></Button>
              </div>
            ))}
            {team && team.members.length === 0 && <p className="text-xs text-muted-foreground">No additional accounts authorised yet.</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-xl">Workspace</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Workspace</span><span className="font-semibold">{ws.name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Owner</span><span className="font-semibold">{user?.name} ({user?.email})</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">AI copy model</span><span className="font-semibold">Gemini 3.1 Pro</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">AI image model</span><span className="font-semibold">Gemini Nano Banana</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Social connections</span><span className="font-semibold text-accent">Simulated (demo mode)</span></div>
        </CardContent>
      </Card>
    </div>
  );
}
